import classNames from "classnames";
import { useEffect, useState } from "react";
import UserService from "services/user.service";
import Select from "react-select";
import {
  ALPHABETS,
  NEW_TIMEZONE,
  PackageList,
  PositionList,
  SalesLeadPackages,
  SalesMarketingAwarenessPackages,
  SeasonList,
  States,
} from "utils/constants";
import { useDebounce } from "hooks/useDebounce";
import { useNotifications } from "hooks/useNotification";
import { useRouter } from "hooks/useRouter";
import { convertTo12HourFormat, hasTimeSlotConflict, validateTimeGap } from "utils/funcs";

const service = new UserService();

/** Comma-separated ZIPs: trim segments and drop empties. */
function zipCodesFromInput(value: string): string[] {
  return String(value || "")
    .split(",")
    .map((z) => z.trim())
    .filter(Boolean);
}

function salesStep2Packages(salesTypeOption: string) {
  if (salesTypeOption === "MARKET AWARENESS") {
    return SalesMarketingAwarenessPackages;
  }
  if (salesTypeOption === "SALES LEAD") {
    return SalesLeadPackages;
  }
  return SalesMarketingAwarenessPackages;
}

/** YYYY-MM-DD — matches saved SocialMediaAds documents from production API. */
function toDateOnlyString(value: unknown): string | null {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split("T")[0];
}

/**
 * UI uses SALES_*_3D / *_7D ids; DB expects BASIC (3d) / PREMIUM (7d) like production payloads.
 */
function salesPackageDbTier(packageName: string) {
  if (!packageName) return { package_name: "BASIC", ad_run_day_count: 3 };
  if (packageName.endsWith("_7D")) {
    return { package_name: "PREMIUM", ad_run_day_count: 7 };
  }
  if (packageName.endsWith("_3D")) {
    return { package_name: "BASIC", ad_run_day_count: 3 };
  }
  return {
    package_name: packageName,
    ad_run_day_count: null as number | null,
  };
}

export const AddMediaAd = () => {
  const [selectedDate, setSelectedDate] = useState("");

  const [state, setState] = useState<any>({
    state: "",
    campaignName: "",
    city_name: "",
    position: "",
    badge_id: "",
    ad_type: "RECRUITING",
    /** Recruiting: '' | 'Scheduling' | 'Screening' */
    isAiEnable: "",
    /** Sales: '' | 'MARKET AWARENESS' | 'SALES LEAD' */
    salesTypeOption: "",
    zipCode: "",
    ad_approval_status: "UNDER REVIEW",
    who_is_paying_for_ad: "", //Myself , Someone else, Split
    bill_split_between: [
      // this will only be uncomment if who_is_paying_for_ad != Myself
    ],
    ad_status: "Upcoming",
    season: "",
    ad_amount: "",
    package_name: "",
    personal_gmail_account: "",
    package_start_date: new Date(),
    package_end_date: "",
    ad_run_day_count: 5,
    recruiting_interview: [
      {
        block_name: "A",
        date: "",
        start_time: "",
        time_zone: "",
        end_time: "",
        office_name: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        interview_type: "",
        custom_location: false,
      },
      {
        block_name: "B",
        date: "",
        start_time: "",
        time_zone: "",
        office_name: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        end_time: "",
        interview_type: "",
        custom_location: false,
      },
    ],
  });
  const [step, setStep] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState("");
  const [minSelectableDate, setMinSelectableDate] = useState("");
  const [feeAcknowledged, setFeeAcknowledged] = useState(false);

  const { push } = useRouter();
  const { successMessage, errorMessage } = useNotifications();

  const debbounced = useDebounce(searchValue, 500);


  useEffect(() => {
    if (state.package_end_date) {
      const endDateObj = new Date(state.package_end_date);

      // Add 1 day to get the next day
      const nextDay = new Date(endDateObj);
      nextDay.setDate(nextDay.getDate() + 1);

      // Add 14 days to set min selectable date (if needed)
      const minDate = new Date(endDateObj);
      minDate.setDate(endDateObj.getDate() + 13);

      // Format to YYYY-MM-DD
      const nextDayStr = nextDay.toISOString().split("T")[0];
      const minDateStr = minDate.toISOString().split("T")[0];

      // Set selected date to one day after package_end_date
      setSelectedDate(nextDayStr);

      // Set min selectable date if needed
      setMinSelectableDate(minDateStr);
    } else {
      setSelectedDate("");
      setMinSelectableDate("");
    }
  }, [state.package_end_date]);

  useEffect(() => {
    setLoadingUsers(true);
    service
      .getSignedReps({
        repName: debbounced,
      })
      .then((resp) => {
        setUsers(
          resp.data.data.map((item) => ({
            value: item._id,
            label: item.rep_name,
          }))
        );
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoadingUsers(false);
      });
  }, [debbounced]);

  const handleInterviewChange = (index, field, value) => {
    const updatedInterviews = [...state.recruiting_interview];
    updatedInterviews[index][field] = value;

    // If changing end_time and start_time exists, validate the 2-hour difference
    if (field === "end_time" && updatedInterviews[index].start_time) {
      const interviews = validateTimeGap(updatedInterviews, updatedInterviews[index].start_time, value, index);
      setState({
        ...state,
        recruiting_interview: interviews
      })
    } else {
      setState({
        ...state,
        recruiting_interview: updatedInterviews,
      });
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setState({
      ...state,
      [name]: value,
    });
  };

  const calculateNewDate = (days, date) => {
    const updatedDate = new Date(date);
    updatedDate.setDate(updatedDate.getDate() + days);
    return updatedDate;
  };

  const onChangePackage = (days, packageName, amount) => {
    setSelectedPackage(days);
    setFeeAcknowledged(false);
    setState({
      ...state,
      package_start_date: state.package_start_date,
      package_end_date: calculateNewDate(days, state.package_start_date),
      package_name: packageName,
      ad_amount: amount,
    });
  };

  const handleChangeStart = (date) => {
    let currentDays;
    if (state.ad_type === "SALES") {
      const list = salesStep2Packages(state.salesTypeOption);
      const selected = list.find((p) => p.packageName === state.package_name);
      currentDays = selected ? selected.day : list[0]?.day ?? 3;
    } else {
      currentDays =
        state?.package_name === "PREMIUM"
          ? 5
          : state?.package_name === "BASIC"
            ? 3
            : 4;
    }
    setState({
      ...state,
      package_start_date: date,
      package_end_date: calculateNewDate(currentDays, date),
    });
  };

  const addInterview = () => {
    // Get the next block letter based on current array length
    const nextBlockLetter = String.fromCharCode(
      65 + state.recruiting_interview.length
    );

    setState({
      ...state,
      recruiting_interview: [
        ...state.recruiting_interview,
        {
          block_name: nextBlockLetter,
          date: "",
          start_time: "",
          end_time: "",
          interview_type: "",
          office_name: "",
          address: "",
          city: "",
          state: "",
          zip_code: "",
          custom_location: false,
        },
      ],
    });
  };

  const validateEmail = (inputEmail) => {
    // Regular expression for email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Check if email is empty
    if (!inputEmail) {
      return "Email is required";
    }

    // Check email format using regex
    if (!emailRegex.test(inputEmail)) {
      return "Please enter a valid email address";
    }

    return "";
  };

  const handleEmailChange = (e) => {
    const inputEmail = e.target.value;
    setState({
      ...state,
      personal_gmail_account: inputEmail,
    });
    setError(validateEmail(inputEmail));
  };

  const removeInterview = (index) => {
    // Create a copy of the interviews array
    const updatedInterviews = [...state.recruiting_interview];

    // Remove the interview at the specified index
    updatedInterviews.splice(index, 1);

    // Reassign block_name for all interviews based on their new position
    updatedInterviews.forEach((interview, idx) => {
      interview.block_name = String.fromCharCode(65 + idx);
    });

    setState({
      ...state,
      recruiting_interview: updatedInterviews,
    });
  };

  const buildPayload = (interviews: typeof state.recruiting_interview) => {
    const zips = zipCodesFromInput(state.zipCode);
    const zipCodeNormalized = zips.length ? zips.join(", ") : state.zipCode;
    const mappedInterviews = interviews.map((item) => ({
      ...item,
      start_time: convertTo12HourFormat(item.start_time),
      end_time: convertTo12HourFormat(item.end_time),
    }));
    const bill_split_between = (state.bill_split_between || []).map(
      (item) => ({
        name: item.label,
        /** Pending until co-payee responds; schema enum: 'Yes' | 'No' | '' */
        isAccepted: "",
        badgeId: item.value,
      })
    );

    if (state.ad_type !== "SALES") {
      return {
        ...state,
        campaignName: String(state.campaignName || "").trim(),
        zipCode: zipCodeNormalized,
        recruiting_interview: mappedInterviews,
        bill_split_between,
      };
    }

    const { package_name: dbPackageName, ad_run_day_count: dbRunDays } =
      salesPackageDbTier(state.package_name);
    const amt = Number(state.ad_amount);

    return {
      ...state,
      badge_id:
        state.badge_id === "" || state.badge_id == null
          ? state.badge_id
          : String(state.badge_id),
      zipCode: zipCodeNormalized,
      season: null,
      position: null,
      isAiEnable: "",
      ad_user_type: "User",
      campaignName: String(state.campaignName || "").trim(),
      package_name: dbPackageName,
      ad_run_day_count: dbRunDays ?? 3,
      ad_amount: Number.isFinite(amt) ? amt : state.ad_amount,
      package_start_date: toDateOnlyString(state.package_start_date),
      package_end_date: toDateOnlyString(state.package_end_date),
      recruiting_interview: [],
      bill_split_between,
      personal_gmail_account: "",
    };
  };


  const submitAd = async (interviews: typeof state.recruiting_interview) => {
    try {
      setSubmitting(true);
      const payload = buildPayload(interviews);
      await service.addMediaAd(payload);
      successMessage("Successfully added");
      push("/media-ads");
    } catch {
      errorMessage("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    const needsPersonalEmail =
      state.ad_type === "RECRUITING" && state.isAiEnable === "Scheduling";

    if (step === 1 && needsPersonalEmail && error) {
      return;
    }
    if (
      step === 1 &&
      (!String(state.campaignName || "").trim() ||
        !state.city_name ||
        !state.who_is_paying_for_ad ||
        !state.badge_id ||
        (needsPersonalEmail &&
          !String(state.personal_gmail_account || "").trim()))
    ) {
      errorMessage("Please fill all fields");
      return;
    }
    if (step === 1 && state.ad_type === "RECRUITING" && !state.isAiEnable) {
      errorMessage("Select Scheduling or Screening under AI Assistant");
      return;
    }
    if (
      step === 1 &&
      state.ad_type === "SALES" &&
      (zipCodesFromInput(state.zipCode).length === 0 || !state.salesTypeOption)
    ) {
      errorMessage(
        "Enter at least one ZIP code (use commas for multiple) and select an ad objective"
      );
      return;
    }
    if (step === 1) {
      setStep(2);
      return;
    }

    if (step === 2) {
      if (state.ad_type === "SALES") {
        if (!state.package_name || !state.package_start_date) {
          errorMessage("Select a package and start date");
          return;
        }
      } else if (
        !state.season ||
        !state.position ||
        !state.package_name ||
        !state.package_start_date
      ) {
        errorMessage("Please fill all fields");
        return;
      }
      if (!feeAcknowledged) {
        errorMessage("Please acknowledge the fee terms to continue");
        return;
      }
    }

    if (step === 2 && state.ad_type === "SALES") {
      await submitAd([]);
      return;
    }

    if (
      step === 2 &&
      state.ad_type === "RECRUITING" &&
      state.isAiEnable === "Screening"
    ) {
      await submitAd([]);
      return;
    }

    if (step === 2) {
      setStep(3);
      return;
    }

    if (step === 3) {
      if (!feeAcknowledged) {
        errorMessage("Please acknowledge the fee terms to continue");
        return;
      }
      const isValid = state.recruiting_interview.every((item) => {
        return (
          item.date &&
          item.start_time &&
          item.end_time &&
          item.interview_type &&
          item.time_zone
        );
      });

      if (!isValid) {
        errorMessage("Please fill all fields");
        return;
      }

      const conflict = hasTimeSlotConflict(state.recruiting_interview);
      if (conflict) {
        errorMessage(conflict);
        return;
      }

      await submitAd(state.recruiting_interview);
    }
  };

  const toggleBtn =
    "flex-1 min-h-10 px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-zinc-900";

  const isRecruitingScreeningOnly =
    state.ad_type === "RECRUITING" && state.isAiEnable === "Screening";
  const totalFormSteps =
    state.ad_type === "SALES" || isRecruitingScreeningOnly ? 2 : 3;

  const feeAmountNum = Number(state.ad_amount);
  const feeDisplay =
    state.package_name && Number.isFinite(feeAmountNum)
      ? String(feeAmountNum)
      : null;

  return (
    <div className="min-h-full w-full flex justify-center px-3 py-5 sm:px-4 sm:py-7 bg-gradient-to-b from-zinc-50 to-zinc-100/80">
      <form
        onSubmit={(e) => e.preventDefault()}
        className="w-full max-w-2xl flex flex-col gap-4 p-4 sm:p-5 bg-white border border-zinc-200/80 rounded-xl shadow-[0_4px_24px_-4px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.02)]"
      >
        <div className="text-center space-y-0.5">
          <p className="text-[11px] font-semibold tracking-[0.18em] text-zinc-400 uppercase">
            Media ad
          </p>
          <h1 className="text-xl sm:text-2xl font-semibold text-zinc-900 tracking-tight">
            {step === 1 && "Location & campaign"}
            {step === 2 && "Ad details"}
            {step === 3 && "Interview scheduling"}
          </h1>
          <p className="text-sm text-zinc-500">
            Step {step} of {totalFormSteps}
          </p>
        </div>

        <div
          className={classNames({
            hidden: step !== 1,
          })}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label className="field-label">
                Campaign name
              </label>
              <input
                type="text"
                required
                name="campaignName"
                value={state.campaignName}
                onChange={handleChange}
                placeholder="e.g. Spring hiring — Dallas"
                className="w-full px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 bg-white border border-zinc-200 rounded-lg focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 focus:outline-none transition-shadow"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="field-label">
                Badge ID
              </label>
              <input
                type="text"
                required
                name="badge_id"
                value={state.badge_id}
                onChange={handleChange}
                placeholder="Enter badge id"
                className="w-full px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 bg-white border border-zinc-200 rounded-lg focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 focus:outline-none transition-shadow"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="field-label">State</label>
              <select
                onChange={handleChange}
                name="state"
                value={state.state}
                className="w-full px-3 py-2 text-sm text-zinc-900 bg-white border border-zinc-200 rounded-lg focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 focus:outline-none transition-shadow"
              >
                <option value="">Select state</option>
                {States.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label className="field-label">
                City name
              </label>
              <input
                type="text"
                name="city_name"
                value={state.city_name}
                required
                onChange={handleChange}
                placeholder="City"
                className="w-full px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 bg-white border border-zinc-200 rounded-lg focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 focus:outline-none transition-shadow"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label className="field-label">
                Who is paying for the ad?
              </label>
              <select
                name="who_is_paying_for_ad"
                value={state.who_is_paying_for_ad}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm text-zinc-900 bg-white border border-zinc-200 rounded-lg focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 focus:outline-none transition-shadow"
              >
                <option value="">Select</option>
                <option value="Myself">Myself</option>
                <option value="Someone else">Someone else</option>
                <option value="Split">Split</option>
              </select>
            </div>
            {state.who_is_paying_for_ad &&
              state.who_is_paying_for_ad !== "Myself" && (
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <label className="field-label">
                    User
                  </label>
                  <Select
                    options={users}
                    placeholder="Search"
                    onChange={(value) =>
                      setState({
                        ...state,
                        bill_split_between: [...state.bill_split_between, value],
                      })
                    }
                    isLoading={loadingUsers}
                    onInputChange={(newValue) => setSearchValue(newValue)}
                    className="react-select"
                    classNamePrefix="react-select"
                  />
                </div>
              )}
          </div>

          <div className="mt-5 pt-5 border-t border-zinc-100 space-y-4">
            <div>
              <p className="text-xs font-semibold tracking-wider text-zinc-500 uppercase mb-2">
                Campaign type
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setFeeAcknowledged(false);
                    setState((s) => ({
                      ...s,
                      ad_type: "RECRUITING",
                      zipCode: "",
                      salesTypeOption: "",
                    }));
                  }}
                  className={classNames(toggleBtn, {
                    "bg-zinc-900 text-white border-zinc-900 shadow-sm":
                      state.ad_type === "RECRUITING",
                    "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50":
                      state.ad_type !== "RECRUITING",
                  })}
                >
                  Recruiting
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    setFeeAcknowledged(false);
                    setState((s) => ({
                      ...s,
                      ad_type: "SALES",
                      isAiEnable: "",
                      personal_gmail_account: "",
                    }));
                  }}
                  className={classNames(toggleBtn, {
                    "bg-zinc-900 text-white border-zinc-900 shadow-sm":
                      state.ad_type === "SALES",
                    "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50":
                      state.ad_type !== "SALES",
                  })}
                >
                  Sales
                </button>
              </div>
            </div>

            {state.ad_type === "RECRUITING" && (
              <div className="rounded-lg bg-zinc-50/80 border border-zinc-100 p-4 space-y-2">
                <p className="text-sm font-semibold text-zinc-900">
                  AI Assistant
                </p>
                <p className="text-xs text-zinc-500">
                  Choose how interviews are handled before continuing.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setState((s) => ({ ...s, isAiEnable: "Scheduling" }))
                    }
                    className={classNames(toggleBtn, {
                      "bg-zinc-900 text-white border-zinc-900 shadow-sm":
                        state.isAiEnable === "Scheduling",
                      "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300":
                        state.isAiEnable !== "Scheduling",
                    })}
                  >
                    Scheduling
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setError("");
                      setState((s) => ({
                        ...s,
                        isAiEnable: "Screening",
                        personal_gmail_account: "",
                      }));
                    }}
                    className={classNames(toggleBtn, {
                      "bg-zinc-900 text-white border-zinc-900 shadow-sm":
                        state.isAiEnable === "Screening",
                      "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300":
                        state.isAiEnable !== "Screening",
                    })}
                  >
                    Screening only
                  </button>
                </div>
                {state.isAiEnable === "Scheduling" && (
                  <div className="pt-3 border-t border-zinc-200/80 space-y-1.5">
                    <label
                      className="text-sm font-medium text-zinc-700"
                      htmlFor="personal_gmail_scheduling"
                    >
                      Enter Personal Gmail Email
                    </label>
                    <input
                      id="personal_gmail_scheduling"
                      type="email"
                      name="personal_gmail_account"
                      value={state.personal_gmail_account}
                      onChange={handleEmailChange}
                      placeholder="Enter Personal Gmail Email"
                      autoComplete="email"
                      className="w-full px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 bg-white border border-zinc-200 rounded-lg focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 focus:outline-none transition-shadow"
                    />
                    {error && (
                      <p className="text-xs text-red-600">{error}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {state.ad_type === "SALES" && (
              <div className="rounded-lg bg-zinc-50/80 border border-zinc-100 p-4 space-y-4">
                <div className="flex flex-col gap-1.5">
                  <label className="field-label">
                    Zip code(s)
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={state.zipCode}
                    onChange={handleChange}
                    placeholder="e.g. 84101 or 84101, 90210, 10001"
                    className="w-full px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 bg-white border border-zinc-200 rounded-lg focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 focus:outline-none transition-shadow"
                  />
                  <p className="text-xs text-zinc-500">
                    Separate multiple ZIP codes with commas.
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-zinc-900 mb-2">
                    Ad objective
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setFeeAcknowledged(false);
                        setState((s) => {
                          const list = SalesMarketingAwarenessPackages;
                          const defaultDays = list[0].day;
                          return {
                            ...s,
                            salesTypeOption: "MARKET AWARENESS",
                            package_name: "",
                            ad_amount: "",
                            package_end_date: s.package_start_date
                              ? calculateNewDate(
                                  defaultDays,
                                  s.package_start_date
                                )
                              : s.package_end_date,
                          };
                        });
                      }}
                      className={classNames(toggleBtn, {
                        "bg-zinc-900 text-white border-zinc-900 shadow-sm":
                          state.salesTypeOption === "MARKET AWARENESS",
                        "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300":
                          state.salesTypeOption !== "MARKET AWARENESS",
                      })}
                    >
                      Marketing Awareness
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFeeAcknowledged(false);
                        setState((s) => {
                          const list = SalesLeadPackages;
                          const defaultDays = list[0].day;
                          return {
                            ...s,
                            salesTypeOption: "SALES LEAD",
                            package_name: "",
                            ad_amount: "",
                            package_end_date: s.package_start_date
                              ? calculateNewDate(
                                  defaultDays,
                                  s.package_start_date
                                )
                              : s.package_end_date,
                          };
                        });
                      }}
                      className={classNames(toggleBtn, {
                        "bg-zinc-900 text-white border-zinc-900 shadow-sm":
                          state.salesTypeOption === "SALES LEAD",
                        "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300":
                          state.salesTypeOption !== "SALES LEAD",
                      })}
                    >
                      Sales Lead
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className={classNames({
            hidden: step !== 2,
          })}
        >
          {state.ad_type !== "SALES" && (
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 flex flex-col gap-1.5 min-w-[150px]">
                <label className="field-label">
                  Season
                </label>
                <select
                  onChange={handleChange}
                  name="season"
                  value={state.season}
                  className="w-full px-3 py-2 text-sm text-zinc-900 bg-white border border-zinc-200 rounded-lg focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 focus:outline-none"
                >
                  <option value="">Select season</option>
                  {SeasonList.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 flex flex-col gap-1.5 min-w-[150px]">
                <label className="field-label">
                  Position
                </label>
                <select
                  onChange={handleChange}
                  name="position"
                  value={state.position}
                  className="w-full px-3 py-2 text-sm text-zinc-900 bg-white border border-zinc-200 rounded-lg focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 focus:outline-none"
                >
                  <option value="">Select position</option>
                  {PositionList.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div
            className={classNames("flex flex-col gap-2", {
              "mt-4": state.ad_type !== "SALES",
              "mt-0": state.ad_type === "SALES",
            })}
          >
            <label className="field-label">
              Ad package
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              {(state.ad_type === "SALES"
                ? salesStep2Packages(state.salesTypeOption)
                : PackageList
              ).map((item) => (
                <button
                  key={item.packageName}
                  type="button"
                  onClick={() =>
                    onChangePackage(item.day, item.packageName, item.price)
                  }
                  className={classNames(
                    "flex-1 flex flex-col items-center justify-center px-3 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2",
                    item.packageName === state.package_name
                      ? "bg-zinc-900 text-white border-zinc-900 shadow-sm"
                      : "bg-white text-zinc-800 border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
                  )}
                >
                  <span className="text-lg font-semibold">${item.price}</span>
                  <span className="text-sm opacity-90">
                    {item.day} {item.day === 1 ? "day" : "days"}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <div className="flex-1 flex flex-col gap-1.5 min-w-[150px]">
              <label className="field-label">
                Start date
              </label>
              <input
                type="date"
                min={new Date().toISOString().split("T")[0]}
                value={
                  state?.package_start_date &&
                  new Date(state.package_start_date)?.toISOString().split("T")[0]
                }
                onChange={(e) => handleChangeStart(e.target.value)}
                className="w-full px-3 py-2 text-sm text-zinc-900 bg-white border border-zinc-200 rounded-lg focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 focus:outline-none"
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5 min-w-[150px]">
              <label className="field-label">
                End date
              </label>
              <input
                type="date"
                disabled
                value={
                  state?.package_end_date &&
                  new Date(state.package_end_date)?.toISOString().split("T")[0]
                }
                onChange={(e) =>
                  setState({ ...state, package_end_date: e.target.value })
                }
                className="w-full px-3 py-2 text-sm text-zinc-600 bg-zinc-100 border border-zinc-200 rounded-lg"
              />
            </div>
          </div>

          {step === 2 && feeDisplay != null && (
            <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50/70 p-4">
              <p className="text-xs font-semibold tracking-wide text-zinc-500 uppercase mb-2">
                Fee acknowledgment
              </p>
              <label className="flex cursor-pointer select-none items-start gap-3">
                <input
                  type="checkbox"
                  checked={feeAcknowledged}
                  onChange={(e) => setFeeAcknowledged(e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 rounded border-zinc-300 text-zinc-900 focus:ring-2 focus:ring-zinc-900 focus:ring-offset-0"
                />
                <span className="text-sm leading-relaxed text-zinc-700">
                  {state.ad_type === "SALES" ? (
                    <>
                      I acknowledge and agree that by submitting a request to
                      run ads, I will be charged a{" "}
                      <strong className="font-semibold text-zinc-900">
                        ${feeDisplay}
                      </strong>{" "}
                      fee, which will be deducted from my October backend check.
                      There are{" "}
                      <strong className="font-semibold text-zinc-900">
                        no overrides
                      </strong>{" "}
                      owed for any of the accounts sold as a result of running
                      these sales ads.
                    </>
                  ) : (
                    <>
                      I acknowledge and agree that by submitting a request to
                      run ads, I will be charged a{" "}
                      <strong className="font-semibold text-zinc-900">
                        ${feeDisplay}
                      </strong>{" "}
                      fee, which will be deducted from my October backend check.
                      There are{" "}
                      <strong className="font-semibold text-zinc-900">
                        no overrides
                      </strong>{" "}
                      owed by the Manager or any of the accounts sold by any
                      applicants generated through this ad campaign.
                    </>
                  )}
                </span>
              </label>
            </div>
          )}
        </div>
        <div
          className={classNames({
            hidden:
              step !== 3 ||
              state.ad_type === "SALES" ||
              isRecruitingScreeningOnly,
          })}
        >
        {state.recruiting_interview.map((item, index) => (
          <div key={`${item.block_name}-${index}`} className="pb-4 border-b border-zinc-100 last:border-0">
            <div className="flex items-center gap-3">
              <h1 className="my-2 text-base font-medium text-zinc-800">
                Interview Session | Block {ALPHABETS[index + 1]}
              </h1>
              {index !== 0 && (
                <button
                  type="button"
                  onClick={() => removeInterview(index)}
                  className="text-red-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                    />
                  </svg>
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex-1 col-span-2 flex flex-col gap-1.5 min-w-[150px]">
                <label className="field-label">Date</label>
                <input
                  type="date"
                  onChange={(e) =>
                    handleInterviewChange(index, "date", e.target.value)
                  }
                  value={item.date}
                  min={selectedDate}
                  // max={minSelectableDate}
                  defaultValue={""}
                  placeholder="Date"
                  className="w-full px-3 py-1.5 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"
                />
              </div>
              <div className="flex-1 flex flex-col gap-1.5 min-w-[150px]">
                <label className="field-label">Start Time</label>
                <input
                  type="time"
                  value={item.start_time}
                  onChange={(e) =>
                    handleInterviewChange(index, "start_time", e.target.value)
                  }
                  placeholder="Start Time"
                  className="w-full px-3 py-1.5 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"
                />
              </div>
              <div className="flex-1 flex flex-col gap-1.5 min-w-[150px]">
                <label className="field-label">End Time</label>
                <input
                  type="time"
                  value={item.end_time}
                  onChange={(e) =>
                    handleInterviewChange(index, "end_time", e.target.value)
                  }
                  placeholder="End Time"
                  className="w-full px-3 py-1.5 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"
                />
              </div>
              <div className="flex-1 flex flex-col gap-1.5 min-w-[150px]">
                <label className="field-label">Timezone</label>
                <select
                  onChange={(e) =>
                    handleInterviewChange(index, "time_zone", e.target.value)
                  }
                  name="position"
                  value={item.time_zone}
                  className="w-full px-3 py-1.5 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"
                >
                  <option value="">Select Timezone</option>
                  {NEW_TIMEZONE.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 flex flex-col gap-1.5 min-w-[150px]">
                <label className="field-label">Zoom</label>
                <select
                  onChange={(e) =>
                    handleInterviewChange(
                      index,
                      "interview_type",
                      e.target.value
                    )
                  }
                  name="interview_type"
                  value={item.interview_type}
                  className="w-full px-3 py-1.5 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"
                >
                  <option value="">Select</option>
                  <option value="Virtual Meeting">Virtual Meeting</option>
                  <option value="In-Person">In-Person</option>
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-zinc-100">
          <div>
            {step === 3 &&
            state.ad_type === "RECRUITING" &&
            state.isAiEnable === "Scheduling" ? (
              <button
                type="button"
                onClick={addInterview}
                className="text-sm font-medium text-zinc-700 px-3 py-2 rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-colors"
              >
                + Add interview block
              </button>
            ) : (
              <span className="hidden sm:inline" />
            )}
          </div>
          <div className="flex justify-end gap-3">
            {step !== 1 && (
              <button
                type="button"
                onClick={() => setStep((prev) => prev - 1)}
                className="px-4 py-2 text-sm font-medium text-zinc-800 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2"
            >
              {step === 3
                ? submitting
                  ? "Submitting…"
                  : "Submit"
                : step === 2 &&
                    (state.ad_type === "SALES" || isRecruitingScreeningOnly)
                  ? submitting
                    ? "Submitting…"
                    : "Submit"
                  : "Next"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
